import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getStoredAccessToken } from '@/modules/auth/helpers/TokenStorage';
import { getBackendBaseUrl } from '@/setup/apiBaseUrl';
import { TaskNotification, TaskNotificationCount } from '../models/TaskModel';
import {
  getTaskNotifications,
  getTaskUnreadCount,
  markTaskNotificationRead,
} from './TaskCRUD';
import { TASK_NOTIFICATIONS_ENABLED } from './taskNotificationConfig';

export interface TaskNotificationState {
  items: TaskNotification[];
  unreadCount: number;
  taskCount: number;
  commentCount: number;
  connected: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: TaskNotificationState = {
  items: [],
  unreadCount: 0,
  taskCount: 0,
  commentCount: 0,
  connected: false,
  loading: false,
  error: null,
};

const COMMENT_TYPES = ['comment_created', 'comment_read'];

const isCommentType = (type: string): boolean => COMMENT_TYPES.includes(type);

const sortNewestFirst = (items: TaskNotification[]): TaskNotification[] =>
  [...items].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

// ==================== ASYNC THUNKS ====================

export const fetchNotifications = createAsyncThunk(
  'taskNotifications/fetchNotifications',
  async (params: { is_read?: boolean } | undefined, { rejectWithValue }) => {
    if (!TASK_NOTIFICATIONS_ENABLED) {
      return [];
    }
    try {
      return await getTaskNotifications(params);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'taskNotifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    if (!TASK_NOTIFICATIONS_ENABLED) {
      return { count: 0, task_count: 0, comment_count: 0 } as TaskNotificationCount;
    }
    try {
      return await getTaskUnreadCount();
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const markRead = createAsyncThunk(
  'taskNotifications/markRead',
  async (id: string, { dispatch, rejectWithValue }) => {
    if (!TASK_NOTIFICATIONS_ENABLED) {
      return id;
    }
    try {
      await markTaskNotificationRead(id);
      // The notification may not be in `items` (e.g. marked from
      // TaskCommentsSection), so refresh the authoritative badge count.
      await dispatch(fetchUnreadCount());
      return id;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// ==================== WEBSOCKET SINGLETON ====================

type DispatchLike = (action: unknown) => unknown;

let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let reconnectAttempts = 0;
let missingTokenRetries = 0;
let activeDispatch: DispatchLike | null = null;

const RECONNECT_BASE_DELAY = 2000;
const RECONNECT_MAX_DELAY = 30000;
const MAX_MISSING_TOKEN_RETRIES = 5;
const POLL_INTERVAL = 60000;
const AUTH_CLOSE_CODE = 4401;

const buildSocketUrl = (): string | null => {
  const token = getStoredAccessToken();
  if (!token) {
    return null;
  }
  const wsBase = getBackendBaseUrl().replace(/^http/i, 'ws');
  return `${wsBase}/ws/tasks/notifications/?token=${encodeURIComponent(token)}`;
};

const clearReconnectTimer = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const startPolling = () => {
  stopPolling();
  pollTimer = setInterval(() => {
    if (activeDispatch) {
      activeDispatch(fetchUnreadCount());
    }
  }, POLL_INTERVAL);
};

const scheduleReconnect = () => {
  if (!activeDispatch) {
    return;
  }
  clearReconnectTimer();
  const delay = Math.min(
    RECONNECT_BASE_DELAY * 2 ** reconnectAttempts,
    RECONNECT_MAX_DELAY
  );
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    openSocket();
  }, delay);
};

const openSocket = () => {
  if (typeof window === 'undefined' || typeof WebSocket === 'undefined') {
    return;
  }
  if (
    socket &&
    (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
  ) {
    return;
  }

  const url = buildSocketUrl();
  if (!url) {
    // Token not available yet (e.g. right after login): retry a bounded
    // number of times instead of silently giving up on real-time.
    if (missingTokenRetries < MAX_MISSING_TOKEN_RETRIES) {
      missingTokenRetries += 1;
      scheduleReconnect();
    }
    return;
  }
  missingTokenRetries = 0;

  let ws: WebSocket;
  try {
    ws = new WebSocket(url);
  } catch {
    scheduleReconnect();
    return;
  }
  socket = ws;

  ws.onopen = () => {
    reconnectAttempts = 0;
    missingTokenRetries = 0;
    if (activeDispatch) {
      activeDispatch(socketConnected(true));
    }
  };

  ws.onmessage = (event: MessageEvent) => {
    try {
      const payload = JSON.parse(String(event.data));
      if (payload && typeof payload === 'object' && payload.id && activeDispatch) {
        activeDispatch(notificationReceived(payload as TaskNotification));
      }
    } catch {
      // Ignore malformed frames; REST polling stays the safety net.
    }
  };

  ws.onerror = () => {
    try {
      ws.close();
    } catch {
      // noop
    }
  };

  ws.onclose = (event: CloseEvent) => {
    if (socket === ws) {
      socket = null;
    }
    if (activeDispatch) {
      activeDispatch(socketConnected(false));
    }
    // Bad/expired token or non-employee: do not hammer the server.
    if (event?.code === AUTH_CLOSE_CODE) {
      stopPolling();
      return;
    }
    scheduleReconnect();
  };
};

export const startNotificationSocket = createAsyncThunk(
  'taskNotifications/startSocket',
  async (_, { dispatch }) => {
    if (!TASK_NOTIFICATIONS_ENABLED) {
      return false;
    }
    activeDispatch = dispatch as DispatchLike;
    reconnectAttempts = 0;
    missingTokenRetries = 0;
    startPolling();
    openSocket();
    return true;
  }
);

export const stopNotificationSocket = createAsyncThunk(
  'taskNotifications/stopSocket',
  async () => {
    clearReconnectTimer();
    stopPolling();
    if (socket) {
      const ws = socket;
      socket = null;
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      try {
        ws.close();
      } catch {
        // noop
      }
    }
    activeDispatch = null;
    return false;
  }
);

// ==================== SLICE ====================

const taskNotificationSlice = createSlice({
  name: 'taskNotifications',
  initialState,
  reducers: {
    notificationReceived: (state, action: PayloadAction<TaskNotification>) => {
      const notification = action.payload;
      if (!notification?.id) {
        return;
      }
      if (state.items.some((item) => item.id === notification.id)) {
        return;
      }

      state.items = sortNewestFirst([
        { ...notification, is_read: false },
        ...state.items,
      ]);
      state.unreadCount += 1;
      if (isCommentType(notification.type)) {
        state.commentCount += 1;
      } else {
        state.taskCount += 1;
      }
    },
    socketConnected: (state, action: PayloadAction<boolean>) => {
      state.connected = action.payload;
    },
    resetTaskNotifications: (state) => {
      state.items = [];
      state.unreadCount = 0;
      state.taskCount = 0;
      state.commentCount = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = sortNewestFirst(action.payload);
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        // Non-employee users get a 400 here; keep the UI empty instead of broken.
        state.items = [];
        state.error = action.error.message || 'Failed to fetch notifications';
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action: PayloadAction<TaskNotificationCount>) => {
        state.unreadCount = action.payload.count;
        state.taskCount = action.payload.task_count;
        state.commentCount = action.payload.comment_count;
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        // Transient failure: keep the last known counts instead of hiding the badge.
        state.error = action.error.message || 'Failed to fetch notification count';
      })
      .addCase(markRead.fulfilled, (state, action: PayloadAction<string>) => {
        // Counters are refreshed authoritatively by the markRead thunk (which
        // also covers notifications not present in `items`); only drop the item.
        const id = action.payload;
        const index = state.items.findIndex((item) => item.id === id);
        if (index >= 0) {
          state.items.splice(index, 1);
        }
      })
      .addCase(markRead.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to mark notification as read';
      });
  },
});

export const { notificationReceived, socketConnected, resetTaskNotifications } =
  taskNotificationSlice.actions;

export default taskNotificationSlice.reducer;
